import { get } from 'lodash';

import { TCustomerFields } from 'modules/checkout/presentation/constants';
import { ICustomer } from 'shared/domain/entities';

type TAddressFieldPair = {
  guardianPath: TCustomerFields;
  customerPath: TCustomerFields;
};

const ADDRESS_FIELD_PAIRS: TAddressFieldPair[] = [
  {
    guardianPath: 'guardian.address.postalCode',
    customerPath: 'address.postalCode',
  },
  {
    guardianPath: 'guardian.address.city.id',
    customerPath: 'address.city.id',
  },
  {
    guardianPath: 'guardian.address.district.id',
    customerPath: 'address.district.id',
  },
  {
    guardianPath: 'guardian.address.street',
    customerPath: 'address.street',
  },
  {
    guardianPath: 'guardian.address.number',
    customerPath: 'address.number',
  },
];

const isFilled = (value: unknown) => Boolean(value);

export type TGuardianAddressMirror = Partial<Record<TCustomerFields, unknown>>;

export type TGuardianAddressAutofill = {
  updates: Array<[TCustomerFields, unknown]>;
  mirror: TGuardianAddressMirror;
};

export default function getGuardianAddressAutofill(
  values: Partial<ICustomer>,
  fields: TCustomerFields[],
  mirror: TGuardianAddressMirror = {},
  savedCustomer?: Partial<ICustomer>,
): TGuardianAddressAutofill {
  return ADDRESS_FIELD_PAIRS.reduce<TGuardianAddressAutofill>(
    (autofill, { guardianPath, customerPath }) => {
      if (!fields.includes(guardianPath)) return autofill;

      const customerValue =
        get(values, customerPath) ?? get(savedCustomer, customerPath);

      if (!isFilled(customerValue)) return autofill;

      const guardianValue = get(values, guardianPath);
      const wasMirrored = guardianPath in autofill.mirror;

      if (!wasMirrored && isFilled(guardianValue)) return autofill;

      if (wasMirrored && guardianValue !== autofill.mirror[guardianPath]) {
        return autofill;
      }

      if (guardianValue === customerValue) return autofill;

      return {
        updates: [...autofill.updates, [guardianPath, customerValue]],
        mirror: { ...autofill.mirror, [guardianPath]: customerValue },
      };
    },
    { updates: [], mirror },
  );
}
